import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { supabase } from '../lib/supabase'
import { Plus, Search, X, ShoppingCart, Copy, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { loadOrderStates, poBaseStatus, DISPLAY } from '../lib/orderStatus'
import HelpLink from '../components/HelpLink'

const EMPTY = { po_number: '', supplier_id: '', order_date: '', expected_date: '', ship_window_start: '', ship_window_end: '', notes: '', status: 'draft' }

export default function PurchaseOrders() {
  const { activeBrand } = useAuth()
  const isBrandAdmin = useIsBrandAdmin()
  const [pos, setPOs] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('active')
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formError, setFormError] = useState('')
  const [poState, setPoState] = useState({})

  useEffect(() => { if (activeBrand) load() }, [activeBrand])

  async function load() {
    setLoading(true)
    const [{ data: poData }, { data: supData }] = await Promise.all([
      supabase.from('purchase_orders').select('*, suppliers(name)').eq('brand_id', activeBrand.id).is('deleted_at', null).order('created_at', { ascending: false }),
      supabase.from('suppliers').select('id, name').eq('brand_id', activeBrand.id).order('name')
    ])
    setPOs(poData || [])
    setSuppliers(supData || [])
    try { setPoState((await loadOrderStates(activeBrand.id)).poState) } catch (e) { /* fall back to stored status */ }
    setLoading(false)
  }

  async function checkDuplicatePO(poNumber, excludeId) {
    let query = supabase.from('purchase_orders').select('id').eq('brand_id', activeBrand.id).eq('po_number', poNumber).is('deleted_at', null)
    if (excludeId) query = query.neq('id', excludeId)
    const { data } = await query
    return data && data.length > 0
  }

  async function save() {
    setFormError('')
    setSaving(true)
    const isDup = await checkDuplicatePO(form.po_number)
    if (isDup) { setFormError('A PO with this number already exists.'); setSaving(false); return }
    const payload = { ...form, brand_id: activeBrand.id, supplier_id: form.supplier_id || null, order_date: form.order_date || null, expected_date: form.expected_date || null, ship_window_start: form.ship_window_start || null, ship_window_end: form.ship_window_end || null }
    const { error } = await supabase.from('purchase_orders').insert(payload)
    if (error) { setFormError('Save failed: ' + error.message); setSaving(false); return }
    setSaving(false); setModal(false); load()
  }

  async function copyPO(po) {
    const newNum = `${po.po_number}-COPY`
    setForm({ po_number: newNum, supplier_id: po.supplier_id || '', order_date: '', expected_date: po.expected_date || '', ship_window_start: po.ship_window_start || '', ship_window_end: po.ship_window_end || '', notes: po.notes || '', status: 'draft' })
    setFormError('')
    setModal(true)
  }

  async function deletePO(po) {
    if (!window.confirm(`Delete PO "${po.po_number}"? This cannot be undone.`)) return
    await supabase.from('purchase_orders').update({ deleted_at: new Date().toISOString() }).eq('id', po.id)
    load()
  }

  async function cancelPO(po) {
    if (!window.confirm(`Cancel PO "${po.po_number}"?`)) return
    await supabase.from('purchase_orders').update({ status: 'cancelled' }).eq('id', po.id)
    load()
  }

  let filtered = pos.filter(p =>
    p.po_number.toLowerCase().includes(search.toLowerCase()) ||
    (p.suppliers?.name || '').toLowerCase().includes(search.toLowerCase())
  )
  // Completed is worked out from the inventory uploads (see lib/orderStatus)
  const stateOf = p => poState[p.id] || poBaseStatus(p.status)
  if (filterStatus === 'active') filtered = filtered.filter(p => ['draft', 'confirmed'].includes(stateOf(p)))
  else if (filterStatus === 'cancelled') filtered = filtered.filter(p => stateOf(p) === 'cancelled')
  else if (filterStatus === 'completed') filtered = filtered.filter(p => stateOf(p) === 'completed')

  return (
    <div className="page">
      <div className="page-header">
        <h1>Purchase Orders<HelpLink section="purchase-orders" /></h1>
        <div className="page-header-actions">
          <div className="search-input-wrap"><Search size={14} /><input className="input" placeholder="Search POs..." value={search} onChange={e => setSearch(e.target.value)} /></div>
          <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All</option>
          </select>
          <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setFormError(''); setModal(true) }}><Plus size={14} />New PO</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : filtered.length === 0 ? <div className="empty-state"><ShoppingCart size={40} /><p>No purchase orders found</p></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>PO Number</th><th>Supplier</th><th>Status</th><th>Order Date</th><th>Ship Window</th><th>Expected</th><th></th></tr></thead>
                <tbody>
                  {filtered.map(po => (
                    <tr key={po.id}>
                      <td><Link to={`/purchase-orders/${po.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500, fontFamily: 'var(--mono)', fontSize: '13px' }}>{po.po_number}</Link></td>
                      <td>{po.suppliers?.name || '—'}</td>
                      <td><span className={`badge badge-${DISPLAY[stateOf(po)].badge}`}>{DISPLAY[stateOf(po)].label}</span></td>
                      <td>{po.order_date ? format(new Date(po.order_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                      <td style={{ fontSize: '12px' }}>
                        {po.ship_window_start && po.ship_window_end ? `${format(new Date(po.ship_window_start + 'T12:00:00'), 'MMM d')} – ${format(new Date(po.ship_window_end + 'T12:00:00'), 'MMM d, yyyy')}` : po.ship_window_start ? format(new Date(po.ship_window_start + 'T12:00:00'), 'MMM d, yyyy') : '—'}
                      </td>
                      <td>{po.expected_date ? format(new Date(po.expected_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button className="btn btn-ghost btn-sm" title="Copy PO" onClick={() => copyPO(po)}><Copy size={13} /></button>
                          {['draft', 'confirmed'].includes(stateOf(po)) && (
                            <button className="btn btn-ghost btn-sm" title="Cancel PO" onClick={() => cancelPO(po)} style={{ color: 'var(--warning)' }}>✕</button>
                          )}
                          {!isBrandAdmin && <button className="btn btn-ghost btn-sm" title="Delete PO" onClick={() => deletePO(po)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>New Purchase Order</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-danger">{formError}</div>}
              <div className="form-grid">
                <div className="form-group"><label className="form-label">PO Number *</label><input className="input" value={form.po_number} onChange={e => setForm(f => ({ ...f, po_number: e.target.value }))} placeholder="PO-2024-001" /></div>
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <select className="select" value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                    <option value="">— Select —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Order Date</label><input className="input" type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">Expected Arrival</label><input className="input" type="date" value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))} /></div>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Ship Window</div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Start</label><input className="input" type="date" value={form.ship_window_start} onChange={e => setForm(f => ({ ...f, ship_window_start: e.target.value }))} /></div>
                <div className="form-group"><label className="form-label">End</label><input className="input" type="date" value={form.ship_window_end} onChange={e => setForm(f => ({ ...f, ship_window_end: e.target.value }))} /></div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.po_number}>{saving ? 'Creating...' : 'Create PO'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
